import axios from 'axios'

import { Http } from './http.init'
import { ResponseWrapper, ErrorWrapper } from './util'
import $store from '../store'
import $router from '../router'
import { eraseCookie, setCookie } from '@/utils/cokkie'
import { BaseService } from './base.service'

const API_URL = process.env.VUE_APP_BASE_API_URL

let BEARER = ''

export class AuthService extends BaseService {
  /**
   ******************************
   * @API
   ******************************
   */

  static async makeLogin ({ email, password }) {
    try {
      const response = await axios.post(`${API_URL}/api/login`, { email, password })
      _setAuthData({
        accessToken: response.data.data.access_token,
      })
      return new ResponseWrapper(response, response.data.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async getCurrent () {
    try {
      const response = await this.request({ auth: true }).get(`/api/auth/profile`)
      await $store.dispatch('user/setCurrentUser', response.data.data)
      await $store.dispatch('auth/setAuth', true);
      return new ResponseWrapper(response, response.data.data)
    } catch (error) {
      _resetAuthData()
      throw new ErrorWrapper(error)
    }
  }

  static async getCurrentWithToken (token) {
    try {
      const response = await this.request({ auth: false, token: token }).get(`/api/auth/profile`)
      return new ResponseWrapper(response, response.data.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async updateProfile (formData) {
    try {
      const response = await this.request({ auth: true }).post(`/api/auth/profile`, formData);
      return new ResponseWrapper(response, response.data.data)
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async makeLogout () {
    try {
      await this.request({ auth: true }).post(`/api/auth/logout`)
      _resetAuthData()
      $router.push({ name: 'login' }).catch(() => {})
    } catch (error) {
      throw new ErrorWrapper(error)
    }
  }

  static async refreshTokens () {
    try {
      const response = await axios.post(`${API_URL}/auth/refresh-tokens`, { withCredentials: true })

      _setAuthData({
        accessToken: response.data.data.accessToken,
        exp: _parseTokenData(response.data.data.accessToken).exp
      })
      return new ResponseWrapper(response, response.data.data)
    } catch (error) {
      _resetAuthData()
      $router.push({ name: 'login' }).catch(() => {})
      throw new ErrorWrapper(error)
    }
  }

  static debounceRefreshTokens = this._debounce(() => {
    return this.refreshTokens()
  }, 100)

  /**
   ******************************
   * @METHODS
   ******************************
   */

  static isAccessTokenExpired () {
    const accessTokenExpDate = $store.state.auth.accessTokenExpDate - 10
    const nowTime = Math.floor(new Date().getTime() / 1000)

    return accessTokenExpDate <= nowTime
  }

  static hasRefreshToken () {
    return Boolean(localStorage.getItem('refreshToken'))
  }

  static setRefreshToken (status) {
    if (!['', 'true'].includes(status)) {
      throw new Error(`setRefreshToken: invalid value ${status}; Expect one of ['', 'true']`)
    }

    localStorage.setItem('refreshToken', status)
  }

  static getBearer () {
    return BEARER
  }

  static setBearer (accessToken) {
    BEARER = `Bearer ${accessToken}`
  }

  /**
   * https://stackoverflow.com/questions/35228052/debounce-function-implemented-with-promises
   * @param inner
   * @param ms
   * @returns {function(...[*]): Promise<unknown>}
   * @private
   */
  static _debounce (inner, ms = 0) {
    let timer = null
    let resolves = []

    return function () {
      clearTimeout(timer)
      timer = setTimeout(() => {
        const result = inner()
        resolves.forEach(r => r(result))
        resolves = []
      }, ms)

      return new Promise(resolve => resolves.push(resolve))
    }
  }
}

/**
 ******************************
 * @private_methods
 ******************************
 */

function _parseTokenData (accessToken) {
  let payload = ''
  let tokenData = {}

  try {
    payload = accessToken.split('.')[1]
    tokenData = JSON.parse(atob(payload))
  } catch (error) {
    throw new Error(error)
  }

  return tokenData
}

function _resetAuthData () {
  $store.commit('user/SET_CURRENT_USER', {})
  $store.commit('auth/SET_AUTH', false)
  // reset tokens
  eraseCookie('access_token')
}

function _setAuthData ({ accessToken, exp } = {}) {
  setCookie("access_token", accessToken)
}
